import React, { useRef, useState } from "react";
import { Formik, Form } from "formik";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import { useQuery } from "@apollo/client/react";
import useAuth from "../../hooks/useAuth";
import useMutation from "../../hooks/useMutation";
import useNotification from "../../hooks/notification";
import CardEditable from "../../components/card/CardEditable";
import Input from "../../components/form/Input";
import AddSchedulerDialog from "./AddSchedulerDialog";
import SchedulerSkeleton from "./SchedulerSkeleton";
import { SCHEDULERS_QUERY, FAVORITE_REPORTS_QUERY } from "../../constants/graphql/queries";
import {
  DELETE_SCHEDULER_MUTATION,
  UPDATE_FAVORITE_REPORT_MUTATION,
  DELETE_FAVORITE_REPORT,
} from "../../constants/graphql/mutations";
import ConfirmDeleteModal from "../../components/images/ConfirmDeleteModal";
import ProjectAutocomplete from "../../components/form/projects/ProjectAutocomplete";
import { toOrdinal } from "../../utils/format";
import useUnsavedChanges from "../../hooks/useUnsavedChanges";
import UnsavedChangesDialog from "../../components/modals/UnsavedChanges/UnsavedChangesDialog";

const styles = {
  dialog: {
    minHeight: "500px",
    backgroundColor: "#f4f6f8",
  },
  dialogTitle: {
    position: "relative",
  },
  closeButton: {
    position: "absolute",
    right: 8,
    top: 8,
  },
  dialogActions: {
    px: 3,
    pb: 2,
    justifyContent: "space-between",
  },
};

const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1);
const MULTI_PROJECT_BILLING_REPORT_TYPES = [
  "multi_project_billing",
  "multi_project_billing_roll_up_only",
  "multi_project_billing_operational_activity",
];

const getFrequency = scheduler => {
  switch (scheduler.frequency) {
    case "daily":
      return "Runs every day";
    case "weekly":
      const days = Array.isArray(scheduler.daysOfWeek)
        ? scheduler.daysOfWeek
        : [scheduler.daysOfWeek];
      const capitalizedDays = days.map(capitalize).join(", ");
      return `Runs every week on ${capitalizedDays}`;
    case "monthly":
      return `Runs every month on the ${toOrdinal(scheduler.dayOfMonth)}`;
  }
};

export default function ReportConfiguration({ open, entity, onClose, setEntities }) {
  const { user } = useAuth();
  const { settings } = user.company;
  const { notifySuccess, notifyError } = useNotification();
  const isMultiProjectBillingReport = MULTI_PROJECT_BILLING_REPORT_TYPES.includes(
    entity?.payload?.report_type
  );
  const [showAddScheduler, setShowAddScheduler] = useState(false);
  const [schedulerToEdit, setSchedulerToEdit] = useState(null);
  const [schedulerToDelete, setSchedulerToDelete] = useState(null);
  const [showDeleteTemplateModal, setShowDeleteTemplateModal] = useState(false);
  const [deleteTemplateLoading, setDeleteTemplateLoading] = useState(false);
  const isAdminOrManager = ["admin", "manager"].includes(user?.role);
  const formikRef = useRef(null);
  const {
    attemptClose,
    confirmDiscard,
    cancelDiscard,
    confirmSave,
    showDialog,
    saving: unsavedSaving,
  } = useUnsavedChanges({
    onClose,
    isDirtyFn: () => !!formikRef.current?.dirty,
    onSave: async () => {
      await formikRef.current?.submitForm();
    },
  });

  const { data, loading, error, refetch } = useQuery(SCHEDULERS_QUERY);
  const allSchedulers = data?.schedulers || [];

  const schedulers = allSchedulers.filter(scheduler =>
    scheduler.reportsSchedulers?.some(
      reportScheduler => reportScheduler.favoriteReport?.id === entity?.id
    )
  );

  const [deleteScheduler] = useMutation(DELETE_SCHEDULER_MUTATION, {
    onSuccess: () => {
      refetch();
    },
    onFailure: errors => {
      console.error("Error deleting scheduler:", errors);
    },
  });

  const [updateFavoriteReport, { loading: updateLoading }] = useMutation(
    UPDATE_FAVORITE_REPORT_MUTATION,
    {
      onSuccess: () => {
        notifySuccess("Report configuration updated successfully");
        onClose();
      },
      onFailure: () => {
        notifyError("Failed to update report configuration");
      },
      refetchQueries: [{ query: FAVORITE_REPORTS_QUERY }],
    }
  );

  const [deleteFavoriteReport] = useMutation(DELETE_FAVORITE_REPORT, {
    onSuccess: () => {
      setDeleteTemplateLoading(false);
      notifySuccess("Template has been successfully deleted");
      if (setEntities) {
        setEntities(prev => prev.filter(entityItem => entityItem.id !== entity.id));
      }
      onClose();
    },
    onFailure: errors => {
      setDeleteTemplateLoading(false);
      notifyError(errors);
    },
  });

  const handleSave = async values => {
    try {
      await updateFavoriteReport({
        variables: {
          id: entity.id,
          payload: { ...entity.payload, name: values.templateName },
          reportNotes: values.reportNotes,
          projectIds: values.projectIds,
        },
      });
    } catch (error) {
      console.error("Failed to update report configuration:", error);
    }
  };

  const handleAddScheduler = () => {
    setShowAddScheduler(true);
  };

  const handleSchedulerAdded = newScheduler => {
    refetch();
    setShowAddScheduler(false);
    setSchedulerToEdit(null);
  };

  const handleEditScheduler = schedulerId => {
    const scheduler = schedulers.find(s => s.id === schedulerId);
    if (scheduler) {
      setSchedulerToEdit(scheduler);
    }
  };

  const handleDeleteScheduler = schedulerId => {
    setSchedulerToDelete(schedulerId);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteScheduler({
        variables: { id: schedulerToDelete },
      });
      setSchedulerToDelete(null);
    } catch (error) {
      console.error("Failed to delete scheduler:", error);
    }
  };

  const handleDeleteTemplate = async () => {
    setDeleteTemplateLoading(true);
    try {
      await deleteFavoriteReport({
        variables: { id: entity.id },
      });
    } catch (error) {
      setDeleteTemplateLoading(false);
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={attemptClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: styles.dialog,
      }}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <Typography fontSize={20} fontWeight={500} color="text.primary" align="center">
          Report Configuration
        </Typography>
        <IconButton onClick={attemptClose} sx={styles.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Formik
        innerRef={formikRef}
        initialValues={{
          templateName: entity?.payload?.name || "",
          reportNotes: entity?.reportNotes,
          projectIds: entity?.favoriteReportsProjects?.map(frp => frp.project?.id) || [],
        }}
        onSubmit={handleSave}
        enableReinitialize
      >
        {({ handleSubmit }) => (
          <Form>
            <DialogContent>
              <Paper sx={{ p: 2, mb: 1.5 }}>
                <Typography pb={2} fontWeight={500} fontSize={18} color="text.primary">
                  Key Details
                </Typography>
                <Grid container mb={2} spacing={1.5}>
                  <Grid size={12}>
                    <Input name="templateName" label="Template Name" type="text" />
                  </Grid>
                  <Grid size={12}>
                    <ProjectAutocomplete
                      fullWidth
                      multiple
                      name="projectIds"
                      label={settings.filter_field2}
                      emptyLabel={`Apply to All ${settings.filter_field3}`}
                      maxSelection={isMultiProjectBillingReport ? 40 : null}
                      allowSelectAll={true}
                    />
                  </Grid>
                </Grid>
                <Box>
                  <Input name="reportNotes" label="Report Notes" type="text" />
                </Box>
              </Paper>

              <Paper sx={{ p: 2 }}>
                <Typography pb={1} fontWeight={500} fontSize={18} color="text.primary">
                  Automated Report Schedule
                </Typography>

                {loading ? (
                  <SchedulerSkeleton />
                ) : error ? (
                  <Typography variant="body2" color="error">
                    Error loading schedulers: {error.message}
                  </Typography>
                ) : (
                  <>
                    <Grid container spacing={2}>
                      {schedulers.map(scheduler => (
                        <Grid key={scheduler.id} size={{ xs: 12, sm: 6 }}>
                          <CardEditable
                            title={
                              scheduler.name || scheduler.reportsSchedulers?.[0]?.project?.name
                            }
                            onEdit={() => handleEditScheduler(scheduler.id)}
                            bottomContent={
                              <>
                                <Typography variant="body2" color="textSecondary">
                                  Next Run:{" "}
                                  {scheduler.nextRunAt
                                    ? new Date(scheduler.nextRunAt).toLocaleDateString()
                                    : "Not scheduled"}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {getFrequency(scheduler)}
                                </Typography>
                              </>
                            }
                          >
                            {scheduler.name && (
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                {scheduler.reportsSchedulers?.[0]?.project?.name}
                              </Typography>
                            )}
                          </CardEditable>
                        </Grid>
                      ))}
                    </Grid>
                    <Button
                      size="medium"
                      variant="outlined"
                      onClick={handleAddScheduler}
                      startIcon={<AddIcon />}
                      sx={{ mt: 2 }}
                    >
                      Add Another Automated Report
                    </Button>
                  </>
                )}
              </Paper>
              {(showAddScheduler || schedulerToEdit) && (
                <AddSchedulerDialog
                  entity={entity}
                  scheduler={schedulerToEdit}
                  onClose={() => {
                    setShowAddScheduler(false);
                    setSchedulerToEdit(null);
                  }}
                  onSchedulerAdded={handleSchedulerAdded}
                  onDelete={
                    schedulerToEdit ? () => handleDeleteScheduler(schedulerToEdit.id) : undefined
                  }
                />
              )}
            </DialogContent>
            <DialogActions sx={styles.dialogActions}>
              {isAdminOrManager && (
                <Button
                  color="error"
                  onClick={() => setShowDeleteTemplateModal(true)}
                  startIcon={<DeleteOutlinedIcon />}
                  disabled={deleteTemplateLoading}
                >
                  Delete Template
                </Button>
              )}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button onClick={attemptClose} color="primary" variant="outlined">
                  Close
                </Button>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  color="primary"
                  disabled={updateLoading}
                  startIcon={updateLoading ? <CircularProgress size={16} /> : null}
                >
                  Save
                </Button>
              </Box>
            </DialogActions>
          </Form>
        )}
      </Formik>
      {schedulerToDelete && (
        <ConfirmDeleteModal
          title="Delete Automated Report"
          descr="Are you sure you want to delete this automated report schedule?"
          onClose={() => setSchedulerToDelete(null)}
          onDelete={handleConfirmDelete}
        />
      )}
      {showDeleteTemplateModal && (
        <ConfirmDeleteModal
          title="Delete Template"
          descr="Are you sure you want to delete this template?"
          onClose={() => setShowDeleteTemplateModal(false)}
          onDelete={handleDeleteTemplate}
          loading={deleteTemplateLoading}
        />
      )}
    </Dialog>
    {showDialog && (
      <UnsavedChangesDialog
        open={showDialog}
        onCancel={cancelDiscard}
        onDiscard={confirmDiscard}
        onSave={confirmSave}
        saving={unsavedSaving}
      />
    )}
    </>
  );
}
